import { StandardEvents } from '@shopify/events';

/**
 * Golden Hour: keeps the low stock line (blocks/ghw-low-stock.liquid) in
 * step with the variant she has ticked.
 *
 * The numbers come from the JSON the block wrote, which holds a quantity
 * only for variants whose stock Shopify tracks. The variant is read from
 * the product form's own id input after every product-select event, the
 * way the pack chooser and the second Add to cart do it. While no size is
 * chosen (ghw-size-pending) that input is empty and the line says nothing.
 *
 * The same words go, smaller, into the sticky bar and under the second Add
 * to cart. Neither is ever on screen together with the line itself: the
 * sticky bar only shows once the buy column has scrolled away, and the
 * second button is at the foot of the page.
 */
class GhwLowStock extends HTMLElement {
  #variants = new Map();
  #abort = new AbortController();

  connectedCallback() {
    try {
      for (const v of JSON.parse(this.querySelector('[data-ghw-stock-json]')?.textContent || '[]')) {
        this.#variants.set(String(v.id), v);
      }
    } catch {
      return;
    }
    const { signal } = this.#abort;
    document.addEventListener(StandardEvents.productSelect, this.#onSelect, { signal });
    document.addEventListener('ghw:size-pending', () => this.#render(), { signal });
    this.#render();
    // The pack chooser may have checked its rows before this had its numbers.
    document.dispatchEvent(new CustomEvent('ghw:stock-ready'));
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get threshold() {
    return Number(this.dataset.threshold || 10);
  }

  /** The stock record for a variant id, or null. Also used by ghw-bundle. */
  stockFor(id) {
    return this.#variants.get(String(id)) ?? null;
  }

  /** The sentence for a variant, or '' when there is nothing true to say. */
  textFor(id) {
    const v = this.stockFor(id);
    if (!v || v.q == null || v.q < 1 || v.q > this.threshold) return '';
    const template = v.q === 1 ? this.dataset.last : this.dataset.only;
    let text = (template || '').replace('[count]', String(v.q)).replace('[variant]', v.label || '');
    if (!v.label) text = text.replace(' in ', '').trim();
    return text;
  }

  /**
   * The same, without the variant: for the sticky bar, which already says
   * "Black / 8.5" on the line above and has room for three words.
   */
  shortFor(id) {
    const v = this.stockFor(id);
    if (!v || v.q == null || v.q < 1 || v.q > this.threshold) return '';
    const template = v.q === 1 ? this.dataset.last : this.dataset.only;
    return (template || '').replace('[count]', String(v.q)).replace(/\s*in \[variant\]/, '').replace('[variant]', '').trim();
  }

  #onSelect = async (event) => {
    try {
      await event.promise;
    } catch {
      /* a newer change will land */
    }
    // After Horizon and the size guard have finished with the new HTML.
    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.#render();
  };

  #currentId() {
    const form = document.querySelector('product-form-component form[action*="/cart/add"]');
    return form?.querySelector('input[name="id"]')?.value || '';
  }

  #render() {
    const text = this.textFor(this.#currentId());
    const target = this.querySelector('[data-ghw-stock-text]');
    // Only when it changes: a status region reads out every change.
    if (target && target.textContent !== text) target.textContent = text;
    this.classList.toggle('ghw-stock--quiet', text === '');

    this.#copy(document.querySelector('sticky-add-to-cart .sticky-add-to-cart__info'), this.shortFor(this.#currentId()));
    const again = document.querySelector('ghw-buy-again');
    const packChosen = document.querySelector('.ghw-bundle__radio:checked')?.closest('[data-count]')?.dataset.count > 1;
    this.#copy(again?.querySelector('.ghw-again__choice')?.parentElement, packChosen ? '' : text, again?.querySelector('.ghw-again__form'));
  }

  /**
   * A small copy of the line inside another element. Horizon rebuilds the
   * sticky bar on every variant change, so the copy is looked up and made
   * again when it is gone.
   */
  #copy(parent, text, before = null) {
    if (!parent) return;
    let copy = parent.querySelector(':scope > .ghw-stock-copy');
    if (!copy) {
      copy = document.createElement('p');
      copy.className = 'ghw-stock-copy';
      copy.innerHTML = '<span class="ghw-stock__dot" aria-hidden="true"></span><span></span>';
      if (before && before.parentElement === parent) parent.insertBefore(copy, before);
      else parent.appendChild(copy);
    }
    copy.hidden = text === '';
    const span = copy.lastElementChild;
    if (span && span.textContent !== text) span.textContent = text;
  }
}

if (!customElements.get('ghw-low-stock')) {
  customElements.define('ghw-low-stock', GhwLowStock);
}
